import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

export const avatarVariants = cva('rocco-avatar relative flex shrink-0', {
    variants: {
        size: {
            square16: 'size-4',
            square20: 'size-5',
            square24: 'size-6',
            square28: 'size-7',
            square32: 'size-8',
            square40: 'size-10',
            square48: 'size-12',
            square56: 'size-14',
            square64: 'size-16',
            square80: 'size-20',
            square112: 'size-28',
            square72: 'size-18',
            w60h90: 'h-90 w-60',
        },
        shape: {
            full: 'rounded-full',
            md: 'rounded-md',
            lg: 'rounded-lg',
            xl: 'rounded-xl',
        },
        border: {
            none: '',
            light: 'border border-gray-200',
            dark: 'border border-gray-900',
        },
        status: {
            active: 'active',
            inactive: 'inactive',
            none: '',
        },
    },
    defaultVariants: {
        size: 'square40',
        shape: 'full',
        border: 'none',
        status: 'none',
    },
});

export const Avatar = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
        VariantProps<typeof avatarVariants> & {
            customBorderColor?: string;
        }
>(
    (
        {
            size,
            shape,
            border,
            status,
            className,
            customBorderColor,
            style,
            ...props
        },
        ref,
    ) => {
        // Determine dot color based on status
        const getDotColor = () => {
            switch (status) {
                case 'active':
                    return '#2E8B11';
                case 'inactive':
                    return '#FF0000';
                default:
                    return null;
            }
        };

        const dotColor = getDotColor();

        const getDotStyles = () => {
            switch (size) {
                case 'square16':
                case 'square20':
                case 'square24':
                    return 'h-2 w-2 -bottom-0.5 -right-0.5 border';
                case 'square28':
                case 'square32':
                case 'square40':
                    return 'h-3 w-3 -bottom-1 -right-1 border';
                case 'square48':
                case 'square56':
                case 'square64':
                    return 'h-4 w-4 bottom-1 right-1 border-[1.5px]';
                case 'square80':
                case 'square112':
                case 'square72':
                    return 'h-5 w-5 bottom-[0.5rem] right-[0.5rem] border-2';
            }
        };

        return (
            <AvatarPrimitive.Root
                ref={ref}
                className={cn(
                    avatarVariants({
                        size,
                        shape,
                        border,
                        status,
                    }),
                    customBorderColor && 'border',
                    className,
                    props.onClick ? 'cursor-pointer' : '',
                )}
                style={{
                    ...(customBorderColor
                        ? { borderColor: customBorderColor }
                        : {}),
                    ...style,
                }}
                {...props}
            >
                {props.children}
                {dotColor && (
                    <span
                        className={cn(
                            'absolute z-10 rounded-full border-white',
                            getDotStyles(),
                        )}
                        style={{
                            backgroundColor: dotColor,
                        }}
                    />
                )}
            </AvatarPrimitive.Root>
        );
    },
);

Avatar.displayName = AvatarPrimitive.Root.displayName;
